import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  MapPin,
  Briefcase,
  Users,
  Lightbulb,
  Zap,
  Globe,
  Search,
  Box,
  BarChart3,
  RotateCw,
  Settings,
  Monitor,
  PenTool,
  Layers,
  Network,
  Megaphone,
  ArrowUpRight,
  ChevronDown,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ALL_OPEN_POSITIONS } from "../data/openPositionsData";

const WHY_SIMPEL_CARDS = [
  {
    icon: Lightbulb,
    iconBg: "bg-[#EBF5FF]",
    iconColor: "text-[#0091FF]",
    title: "Think Beyond the Obvious",
    desc: "We challenge assumptions, ask better questions, and look for smarter ways forward.",
  },
  {
    icon: Zap,
    iconBg: "bg-[#F3E8FF]",
    iconColor: "text-[#A855F7]",
    title: "Build With Purpose",
    desc: "Work on technology that solves real business problems — not technology for technology's sake.",
  },
  {
    icon: Users,
    iconBg: "bg-[#FEF3C7]",
    iconColor: "text-[#D97706]",
    title: "Work Together",
    desc: "Strategy, design, engineering, AI, and business expertise come together as one team.",
  },
  {
    icon: Sparkles,
    iconBg: "bg-[#DCFCE7]",
    iconColor: "text-[#16A34A]",
    title: "Keep Evolving",
    desc: "AI is changing quickly. Learn, experiment, and grow with every project.",
  },
  {
    icon: Globe,
    iconBg: "bg-[#FFE4E6]",
    iconColor: "text-[#E11D48]",
    title: "Make an Impact",
    desc: "Work with businesses and teams solving meaningful challenges across industries.",
  },
];

const LIFE_AT_SIMPEL_TABS = [
  {
    id: "01",
    label: "01 — Explore",
    title: "01 — Explore",
    desc: "Ask better questions. Discover new possibilities. Never stop learning.",
    img: "/images/careers/explore.png",
  },
  {
    id: "02",
    label: "02 — Collaborate",
    title: "02 — Collaborate",
    desc: "The best solutions aren't built in silos. We bring different perspectives together to make ideas stronger.",
    img: "/images/careers/collaborate.png",
  },
  {
    id: "03",
    label: "03 — Experiment",
    title: "03 — Experiment",
    desc: "From AI Labs to new technologies, we encourage people to test, prototype, and learn by doing.",
    img: "/images/careers/experiment.png",
  },
  {
    id: "04",
    label: "04 — Learn",
    title: "04 — Learn",
    desc: "Every project is an opportunity to develop new skills, share knowledge, and grow with the people around you.",
    img: "/images/careers/learn.png",
  },
  {
    id: "05",
    label: "05 — Celebrate",
    title: "05 — Celebrate",
    desc: "We celebrate the ideas, milestones, and people who help move Simpel forward.",
    img: "/images/careers/celebrate.png",
  },
];

const HOW_WE_WORK_STEPS = [
  {
    icon: Search,
    title: "Understand",
    desc: "Start with the problem, not the technology.",
  },
  {
    icon: Lightbulb,
    title: "Explore",
    desc: "Challenge assumptions and discover possibilities.",
  },
  {
    icon: Box,
    title: "Build",
    desc: "Turn ideas into working solutions.",
  },
  {
    icon: BarChart3,
    title: "Learn",
    desc: "Measure what works and what doesn't.",
  },
  {
    icon: RotateCw,
    title: "Evolve",
    desc: "Keep improving as the business changes.",
  },
];

const IMPACT_DOMAINS = [
  {
    icon: Settings,
    title: "AI & Engineering",
    desc: "AI systems, agents, models and integrations",
  },
  {
    icon: Monitor,
    title: "Technology",
    desc: "Platforms, architecture and digital products",
  },
  {
    icon: Users,
    title: "Strategy & Consulting",
    desc: "Business transformation and opportunity discovery",
  },
  {
    icon: PenTool,
    title: "Product & Design",
    desc: "Human-centered digital and AI experiences",
  },
  {
    icon: Layers,
    title: "Data",
    desc: "Intelligence, analytics and decision systems",
  },
  {
    icon: Network,
    title: "Delivery & Operations",
    desc: "Turning ideas into scalable solutions",
  },
  {
    icon: BarChart3,
    title: "Sales & Partnerships",
    desc: "Helping businesses discover what's possible",
  },
  {
    icon: Megaphone,
    title: "Marketing & Brand",
    desc: "Telling the Simpel story",
  },
];

const Career = () => {
  const navigate = useNavigate();
  const [activeLifeTab, setActiveLifeTab] = useState("01");
  const [roleSearch, setRoleSearch] = useState("");
  const [selectedDept, setSelectedDept] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedExperience, setSelectedExperience] = useState("All");
  const [selectedWorkMode, setSelectedWorkMode] = useState("All");

  const [isIntroduceModalOpen, setIsIntroduceModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleIntroduceSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    toast.success("Thank you! Your details have been submitted. We'll be in touch soon.");
    setFormData({ fullName: "", phone: "", email: "", message: "" });
    setIsIntroduceModalOpen(false);
  };

  const isFiltering =
    roleSearch.trim() !== "" ||
    selectedDept !== "All" ||
    selectedLocation !== "All" ||
    selectedExperience !== "All" ||
    selectedWorkMode !== "All";

  const sourceRoles = isFiltering
    ? ALL_OPEN_POSITIONS
    : ALL_OPEN_POSITIONS.filter((r) => r.featured);

  const filteredRoles = sourceRoles.filter((role) => {
    const query = roleSearch.toLowerCase().trim();
    const matchesSearch =
      query === "" ||
      role.title.toLowerCase().includes(query) ||
      role.dept.toLowerCase().includes(query) ||
      role.desc.toLowerCase().includes(query) ||
      (role.tags && role.tags.some((t) => t.toLowerCase().includes(query))) ||
      (role.skills && role.skills.some((s) => s.toLowerCase().includes(query)));

    const matchesDept =
      selectedDept === "All" ||
      role.dept.toLowerCase().includes(selectedDept.toLowerCase());

    const matchesLoc =
      selectedLocation === "All" ||
      role.loc.toLowerCase().includes(selectedLocation.toLowerCase());

    const matchesExp =
      selectedExperience === "All" ||
      role.experience.toLowerCase().includes(selectedExperience.toLowerCase());

    const matchesMode =
      selectedWorkMode === "All" ||
      role.workMode.toLowerCase().includes(selectedWorkMode.toLowerCase());

    return (
      matchesSearch &&
      matchesDept &&
      matchesLoc &&
      matchesExp &&
      matchesMode
    );
  });

  const currentLifeTab =
    LIFE_AT_SIMPEL_TABS.find((t) => t.id === activeLifeTab) ||
    LIFE_AT_SIMPEL_TABS[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToOpenRoles = () => {
    const el = document.getElementById("open-roles");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      toast.info("Showing open positions");
    }
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />
      <main>
        {/* Career Hero Section */}
        <section
          className="relative flex w-full items-center overflow-hidden"
          style={{
            backgroundColor: "rgba(1, 7, 16, 1)",
            minHeight: "613px",
          }}
        >
          {/* Panoramic Office Image on Right */}
          <div className="absolute inset-y-0 right-0 h-full w-full lg:w-[65%] xl:w-[62%] 2xl:w-[60%] overflow-hidden">
            <img
              src="/images/careers/hero.png"
              alt="Simpel AI Team Collaborating"
              className="h-full w-full object-cover object-center lg:object-left-center"
            />
            {/* Linear Gradient matching Figma inspect: rgba(1, 7, 16, 1) to rgba(2, 13, 30, 0) */}
            <div
              className="absolute inset-0 hidden lg:block"
              style={{
                background:
                  "linear-gradient(90deg, rgba(1, 7, 16, 1) 0%, rgba(1, 7, 16, 0.92) 28%, rgba(1, 7, 16, 0.55) 55%, rgba(2, 13, 30, 0) 100%)",
              }}
            />
            {/* Mobile / Tablet dark vignette overlay */}
            <div
              className="absolute inset-0 lg:hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(1, 7, 16, 0.92) 0%, rgba(1, 7, 16, 0.75) 50%, rgba(1, 7, 16, 0.95) 100%)",
              }}
            />
          </div>

          {/* Ambient Blue Glow in Background */}
          <div
            className="pointer-events-none absolute"
            style={{
              width: 1000,
              height: 420,
              left: -200,
              top: -80,
              background: "#173862",
              filter: "blur(110px)",
              opacity: 0.55,
            }}
          />

          {/* Hero Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-24 sm:py-28 lg:py-32 lg:px-[70px]">
            <div className="flex max-w-[580px] xl:max-w-[620px] flex-col gap-6">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                CAREER AT SIMPEL AI
              </p>

              <h1 className="text-[44px] sm:text-[56px] lg:text-[62px] xl:text-[66px] font-bold leading-[1.06] text-white tracking-tight">
                Build what's next.{" "}
                <span className="text-[#00D2FF]">Together.</span>
              </h1>

              <p className="max-w-[496px] text-[15px] sm:text-[16px] leading-[26px] text-white/80">
                At Simpel, we bring together business, technology, AI, and human
                creativity to solve meaningful problems and create what comes
                next.
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-5">
                <button
                  onClick={() => navigate("/careers/open-positions")}
                  className="flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-semibold text-[#0F172A] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0px_4px_32px_rgba(0,210,255,0.7)] cursor-pointer"
                  style={{ boxShadow: "0px 4px 26px rgba(0,194,255,0.45)" }}
                >
                  Explore open roles <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Simpel Section */}
        <section
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col gap-3">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                WHY SIMPEL?
              </p>
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold text-[#0F172A] tracking-tight">
                More than a job. A place to build.
              </h2>
              <p className="text-[15px] sm:text-[16px] text-[#64748B] max-w-[700px] leading-relaxed">
                We're creating an environment where curious people can turn ambitious ideas into real-world impact.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 xl:gap-6">
              {WHY_SIMPEL_CARDS.map((item) => (
                <div
                  key={item.title}
                  className="rounded-[22px] bg-white border border-[#E2E8F0] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] hover:-translate-y-1 hover:shadow-[0_10px_26px_rgba(15,23,42,0.06)] transition-all duration-300 flex flex-col justify-between min-h-[290px]"
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-[14px] ${item.iconBg} ${item.iconColor}`}>
                    <item.icon className="h-5 w-5" strokeWidth={2.3} />
                  </span>

                  <div className="mt-8">
                    <h3 className="text-[18px] font-bold leading-snug text-[#0F172A]">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[13.5px] leading-[21px] text-[#64748B]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Life at Simpel Section */}
        <section
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(1, 7, 16, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Top Header */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between mb-14 lg:mb-16">
              <div className="flex flex-col gap-3">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                  LIFE AT SIMPEL
                </p>
                <h2 className="text-[32px] sm:text-[40px] lg:text-[44px] font-bold tracking-tight text-white leading-[1.12]">
                  Different minds. One ambition.
                </h2>
              </div>
              <p className="text-[14px] sm:text-[15px] leading-[23px] text-[#94A3B8] max-w-[430px] lg:border-l lg:border-[#1E293B] lg:pl-8">
                At Simpel, we believe the best ideas come from people who think differently, challenge assumptions, and bring their own perspective to the table.
              </p>
            </div>

            {/* Interactive Grid: Left Tabs / Right Image */}
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-10 xl:gap-14">
              {/* Left Column: Tab List (5 cols) */}
              <div className="flex flex-col gap-2.5 sm:gap-3 lg:col-span-5">
                {LIFE_AT_SIMPEL_TABS.map((tab) => {
                  const isActive = activeLifeTab === tab.id;

                  if (isActive) {
                    return (
                      <motion.div
                        key={tab.id}
                        layoutId="activeLifeTabCard"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        className="rounded-[20px] sm:rounded-[22px] p-6 sm:p-7 shadow-[0_8px_32px_rgba(0,210,238,0.25)]"
                        style={{
                          background: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
                        }}
                      >
                        <motion.h3
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                          className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] leading-tight"
                        >
                          {tab.title}
                        </motion.h3>
                        <motion.p
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.05 }}
                          className="mt-3.5 text-[13.5px] sm:text-[14px] leading-[22px] text-[#0F172A]/85 font-medium max-w-[340px]"
                        >
                          {tab.desc}
                        </motion.p>
                      </motion.div>
                    );
                  }

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveLifeTab(tab.id)}
                      className="group flex w-full items-center justify-between rounded-[18px] px-6 py-4 text-left transition-all duration-300 hover:bg-white/[0.06] hover:translate-x-1 cursor-pointer"
                    >
                      <span className="text-[18px] sm:text-[20px] font-bold text-white transition-colors duration-300 group-hover:text-[#00D2EE]">
                        {tab.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Right Column: Active Tab Image (7 cols) with exact 688x499, 20px radius, 5px white border */}
              <div className="flex justify-center lg:justify-end lg:col-span-7">
                <div
                  className="relative w-full max-w-[688px] h-[340px] sm:h-[420px] lg:h-[499px] overflow-hidden shadow-[0_16px_50px_rgba(0,0,0,0.6)]"
                  style={{
                    borderRadius: "20px",
                    borderWidth: "5px",
                    borderStyle: "solid",
                    borderColor: "rgba(255, 255, 255, 1)",
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentLifeTab.id}
                      src={currentLifeTab.img}
                      alt={currentLifeTab.title}
                      initial={{ opacity: 0, scale: 0.95, filter: "blur(6px)" }}
                      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, scale: 1.04, filter: "blur(6px)" }}
                      transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full w-full object-cover"
                      style={{ borderRadius: "15px" }}
                    />
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How We Work Section */}
        <section
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-12 lg:gap-14">
              {/* Left Header */}
              <div className="flex flex-col gap-3 lg:max-w-[340px] shrink-0">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#78889B]">
                  HOW WE WORK
                </p>
                <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight text-[#0B1527] leading-[1.15]">
                  A simple way to create meaningful impact.
                </h2>
              </div>

              {/* Right Steps - 5 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-6 xl:gap-8 flex-1">
                {HOW_WE_WORK_STEPS.map((step) => (
                  <div key={step.title} className="flex flex-col items-start group">
                    {/* Step Icon in soft light-blue circle */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#BAE6FD] bg-[#EAF8FE] text-[#0284C7] transition-transform duration-300 group-hover:scale-110 shadow-sm">
                      <step.icon className="h-5 w-5" strokeWidth={2.1} />
                    </div>

                    {/* Title & Description */}
                    <h3 className="mt-5 text-[17px] font-bold text-[#0F172A] leading-snug">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-[21px] text-[#64748B]">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Where You Could Make an Impact Section */}
        <section
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(5, 8, 17, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Section Header */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-14">
              <div className="flex flex-col gap-3">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                  WHERE YOU COULD MAKE AN IMPACT
                </p>
                <h2 className="text-[32px] sm:text-[40px] lg:text-[44px] font-bold tracking-tight text-white leading-[1.12]">
                  Find your place at Simpel.
                </h2>
              </div>
              <div className="lg:border-l lg:border-[#2E3C51] lg:pl-8">
                <p className="text-[14px] sm:text-[15px] leading-[23px] text-[#94A3B8] max-w-[420px]">
                  Across multiple disciplines, we work together to help businesses unlock the possibilities of AI and technology.
                </p>
              </div>
            </div>

            {/* 8 Cards Grid (2 rows of 4) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {IMPACT_DOMAINS.map((item) => (
                <div
                  key={item.title}
                  className="group flex flex-col justify-start rounded-[16px] transition-all duration-300 hover:border-[#00D2EE]/40 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(0,0,0,0.5)] cursor-default"
                  style={{
                    backgroundColor: "rgba(12, 19, 34, 1)",
                    border: "1px solid rgba(28, 38, 56, 1)",
                    borderRadius: "16px",
                    paddingTop: "20px",
                    paddingRight: "20px",
                    paddingBottom: "39.5px",
                    paddingLeft: "20px",
                    minHeight: "163.5px",
                  }}
                >
                  {/* Top Icon */}
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-white/[0.08] bg-white/[0.04] text-white/90 transition-colors group-hover:border-[#00D2EE]/40 group-hover:text-[#00D2EE]">
                    <item.icon className="h-4 w-4" strokeWidth={1.9} />
                  </div>

                  {/* Content with 6px gap */}
                  <div className="mt-3.5 flex flex-col gap-[6px]">
                    <h3 className="text-[16px] font-bold text-white leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-[12.5px] sm:text-[13px] leading-[18px] text-[#7E8B9F]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Opportunities Section */}
        <section
          id="open-roles"
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header: Title on Left, Link on Right */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="flex flex-col gap-2.5">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                  FEATURED OPPORTUNITIES
                </p>
                <h2 className="text-[32px] sm:text-[40px] lg:text-[44px] font-bold tracking-tight text-[#0B1527] leading-[1.12]">
                  Find your next opportunity.
                </h2>
              </div>
              <button
                onClick={() => navigate("/careers/open-positions")}
                className="group inline-flex items-center gap-1.5 text-[14px] sm:text-[15px] font-semibold text-[#007AFF] hover:text-[#0056B3] transition-colors cursor-pointer self-start sm:self-auto"
              >
                View all roles
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Search Bar & Filter Controls matching exact specs */}
            <div className="mt-8 mb-10 flex flex-wrap items-center gap-3">
              {/* Search Bar: Width Fill (429px), Height Hug (38px), Radius 12px, Border 1px, Padding Top 10, Right 16, Bottom 11, Left 40 */}
              <div className="relative w-full md:max-w-[429px] flex-1">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                <input
                  type="text"
                  placeholder="Search by role, skill or keyword..."
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                  className="h-[38px] w-full rounded-[12px] border border-[rgba(226,232,240,1)] bg-[rgba(248,250,252,1)] pt-[10px] pb-[11px] pr-[16px] pl-[40px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] focus:bg-white"
                />
              </div>

              {/* Department Filter */}
              <div className="relative">
                <select
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  className="h-[38px] appearance-none rounded-[12px] border border-[rgba(226,232,240,1)] bg-[rgba(248,250,252,1)] pl-4 pr-8 text-[13px] text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                >
                  <option value="All">Department</option>
                  <option value="AI & Engineering">AI & Engineering</option>
                  <option value="Product & Design">Product & Design</option>
                  <option value="Strategy & Consulting">Strategy & Consulting</option>
                  <option value="Data">Data</option>
                  <option value="Technology">Technology</option>
                  <option value="Marketing & Brand">Marketing & Brand</option>
                  <option value="People & Culture">People & Culture</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              </div>

              {/* Location Filter */}
              <div className="relative">
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="h-[38px] appearance-none rounded-[12px] border border-[rgba(226,232,240,1)] bg-[rgba(248,250,252,1)] pl-4 pr-8 text-[13px] text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                >
                  <option value="All">Location</option>
                  <option value="Bengaluru">Bengaluru / Hybrid</option>
                  <option value="Multiple">Multiple locations</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              </div>

              {/* Experience Filter */}
              <div className="relative">
                <select
                  value={selectedExperience}
                  onChange={(e) => setSelectedExperience(e.target.value)}
                  className="h-[38px] appearance-none rounded-[12px] border border-[rgba(226,232,240,1)] bg-[rgba(248,250,252,1)] pl-4 pr-8 text-[13px] text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                >
                  <option value="All">Experience</option>
                  <option value="Entry">Entry</option>
                  <option value="Mid-Level">Mid-Level</option>
                  <option value="Mid-Senior">Mid-Senior</option>
                  <option value="Senior">Senior</option>
                  <option value="Lead">Lead</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              </div>

              {/* Work Mode Filter */}
              <div className="relative">
                <select
                  value={selectedWorkMode}
                  onChange={(e) => setSelectedWorkMode(e.target.value)}
                  className="h-[38px] appearance-none rounded-[12px] border border-[rgba(226,232,240,1)] bg-[rgba(248,250,252,1)] pl-4 pr-8 text-[13px] text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] focus:border-[#007AFF] cursor-pointer"
                >
                  <option value="All">Work mode</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Remote">Remote</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
              </div>
            </div>

            {/* Grid of 4 Cards: Role Cards + 1 Dark Intro Card */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {filteredRoles.length === 0 ? (
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex flex-col items-center justify-center rounded-[16px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-8 text-center min-h-[202.5px]">
                  <p className="text-[15px] font-semibold text-[#0F172A]">
                    No roles found matching &ldquo;{roleSearch}&rdquo;
                  </p>
                  <p className="mt-1.5 text-[13px] text-[#64748B] max-w-[420px]">
                    Try searching by another skill (e.g., Python, React, Figma, LLMs, SQL) or clear filters to view all opportunities.
                  </p>
                  <button
                    onClick={() => {
                      setRoleSearch("");
                      setSelectedDept("All");
                      setSelectedLocation("All");
                      setSelectedExperience("All");
                      setSelectedWorkMode("All");
                    }}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#007AFF] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#0056B3] transition-colors cursor-pointer"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                filteredRoles.map((role) => (
                  <div
                    key={role.id || role.title}
                    onClick={() => {
                      if (role.link) {
                        navigate(role.link);
                      } else {
                        navigate(`/careers/${role.id}`);
                      }
                    }}
                    className="group flex flex-col justify-between rounded-[16px] shadow-sm transition-all duration-300 hover:border-[#007AFF]/60 hover:shadow-md cursor-pointer"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(226, 232, 240, 1)",
                      borderRadius: "16px",
                      padding: "24px",
                      minHeight: "202.5px",
                    }}
                  >
                    {/* Top: Title & Arrow */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug group-hover:text-[#007AFF] transition-colors">
                          {role.title}
                        </h3>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-[#CBD5E1] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#007AFF]" />
                      </div>

                      {/* Department | Location */}
                      <div className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
                        <span className="font-semibold text-[#007AFF]">
                          {role.dept}
                        </span>
                        <span className="text-[#CBD5E1]">|</span>
                        <span className="text-[#64748B]">{role.loc}</span>
                      </div>

                      {/* Description */}
                      <p className="mt-1 text-[13px] sm:text-[13.5px] leading-[20px] text-[#64748B]">
                        {role.desc}
                      </p>
                    </div>

                    {/* Bottom: View Role Action */}
                    <div className="mt-5 pt-1">
                      <span className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-[#007AFF] group-hover:gap-2 transition-all">
                        View Role <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                ))
              )}

              {/* 4th Card: Dark Card (Specs from Screenshot 4) */}
              <div
                className="flex flex-col justify-between rounded-[16px] shadow-sm transition-all duration-300 hover:border-white/30"
                style={{
                  backgroundColor: "rgba(5, 8, 17, 1)",
                  border: "1px solid rgba(30, 41, 59, 1)",
                  borderRadius: "16px",
                  padding: "24px",
                  minHeight: "215.5px",
                }}
              >
                <div>
                  <h3 className="text-[16px] sm:text-[17px] font-bold text-white leading-snug">
                    Don't see your role? We'd still like to hear from you.
                  </h3>
                  <p className="mt-2.5 text-[12.5px] sm:text-[13px] leading-[20px] text-[#94A3B8]">
                    Tell us what you do, what you're passionate about, and what you'd like to build.
                  </p>
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => setIsIntroduceModalOpen(true)}
                    className="flex h-[40px] w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-[#1E293B] text-[13.5px] font-semibold text-white transition-all duration-300 hover:bg-[#283548] hover:border-white/20 hover:scale-[1.02] cursor-pointer"
                  >
                    Introduce Yourself <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cognitive Enterprise Banner Section */}
        <section className="relative w-full overflow-hidden bg-[#020813] py-24 sm:py-28 lg:py-32">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="/images/careers/cognitive-enterprise-banner.png"
              alt="Build the Cognitive Enterprise with Simpel AI"
              className="h-full w-full object-cover object-right lg:object-center"
            />
            {/* Subtle mobile overlay for readability */}
            <div
              className="absolute inset-0 lg:hidden"
              style={{
                background:
                  "linear-gradient(90deg, rgba(2, 8, 19, 0.95) 0%, rgba(2, 8, 19, 0.75) 50%, rgba(2, 8, 19, 0.25) 100%)",
              }}
            />
          </div>

          {/* Foreground Content */}
          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="max-w-[560px] flex flex-col gap-3.5">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#8E9CAB]">
                CAREERS AT SIMPEL AI
              </p>
              <h2 className="text-[32px] sm:text-[42px] lg:text-[46px] font-bold tracking-tight text-white leading-[1.14]">
                Want to build the Cognitive Enterprise with us?
              </h2>
              <p className="mt-1 text-[14px] sm:text-[15px] leading-[23px] text-[#94A3B8]">
                Work on real problems. Learn from great people. Create impact.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Introduce Yourself Modal */}
      <AnimatePresence>
        {isIntroduceModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsIntroduceModalOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
            />

            {/* Modal Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-[520px] rounded-[26px] bg-white p-7 sm:p-9 shadow-2xl"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsIntroduceModalOpen(false)}
                className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-[#64748B] hover:bg-gray-100 hover:text-[#0F172A] transition-colors cursor-pointer"
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </button>

              {/* Title & Description */}
              <h3 className="text-[26px] sm:text-[28px] font-bold text-[#0F172A] leading-tight">
                Introduce Yourself
              </h3>
              <p className="mt-2 text-[14px] sm:text-[15px] text-[#556376] leading-relaxed">
                Please fill out the form below with your details and message to contact us.
              </p>

              {/* Form matching screenshot layout */}
              <form onSubmit={handleIntroduceSubmit} className="mt-6 flex flex-col gap-3.5">
                {/* Full Name & Phone Number Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    required
                    className="w-full rounded-[14px] bg-[#E5E7EB]/80 px-4 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                  />
                  <div className="flex items-center rounded-[14px] bg-[#E5E7EB]/80 overflow-hidden focus-within:bg-[#E5E7EB] focus-within:ring-2 focus-within:ring-[#00D2EE]/40 transition-all">
                    <span className="px-3.5 text-[14px] font-semibold text-[#6B7280] border-r border-[#D1D5DB] select-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full bg-transparent px-3.5 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none"
                    />
                  </div>
                </div>

                {/* Email Address */}
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full rounded-[14px] bg-[#E5E7EB]/80 px-4 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                />

                {/* Tell us about yourself */}
                <textarea
                  rows={4}
                  placeholder="Tell us about yourself..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full resize-none rounded-[16px] bg-[#E5E7EB]/80 p-4 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                />

                {/* Submit Button */}
                <div className="mt-2 flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-full px-7 py-3 text-[14.5px] font-bold text-[#0F172A] transition-all hover:scale-105 active:scale-95 shadow-md hover:shadow-lg cursor-pointer"
                    style={{
                      background:
                        "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
                    }}
                  >
                    Submit <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Career;
